import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Check, X } from 'lucide-react';
import { Button } from "@/components/ui/button";

interface ProfileRiskAssessmentProps {
  data: any;
  updateData: (data: any) => void;
}

const ProfileRiskAssessment = ({ data, updateData }: ProfileRiskAssessmentProps) => {
  const [formData, setFormData] = useState({
    marketDropResponse: data.marketDropResponse || '',
    allocationPreference: data.allocationPreference || [33, 33, 34], // Default allocations
    investmentPriority: data.investmentPriority || '',
    investmentFamiliarity: data.investmentFamiliarity || 3, // Scale 1-5
    timeHorizon: data.timeHorizon || ''
  });
  
  // Track which allocation sliders are locked (fixed)
  const [lockedSliders, setLockedSliders] = useState<boolean[]>([false, false, false]);

  useEffect(() => {
    updateData(formData);
  }, [formData, updateData]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle lock state for a slider
  const toggleLock = (index: number) => {
    setLockedSliders(prev => {
      const newLocked = [...prev];
      newLocked[index] = !newLocked[index];
      return newLocked;
    });
  };

  // Handle allocation sliders in a way that allows fixing certain values
  const handleAllocationChange = (index: number, newValue: number) => {
    const currentAllocations = [...formData.allocationPreference];
    const allIndices = [0, 1, 2];
    
    // Find which sliders are unlocked (can be adjusted)
    const unlockedIndices = allIndices.filter(i => i !== index && !lockedSliders[i]);
    
    // If all other sliders are locked, we can't adjust
    if (unlockedIndices.length === 0 && lockedSliders[index]) {
      return; // Can't make any changes if everything is locked
    }
    
    // Calculate the current total
    const currentTotal = currentAllocations.reduce((sum, val) => sum + val, 0);
    
    // Set the new value for the slider being adjusted
    currentAllocations[index] = newValue;
    
    // Calculate how much we need to adjust other sliders to maintain 100% total
    const newTotal = currentAllocations.reduce((sum, val) => sum + val, 0);
    const difference = currentTotal - newTotal; // How much we need to distribute
    
    if (unlockedIndices.length > 0) {
      // Distribute the difference proportionally among unlocked sliders
      const adjustmentPerSlider = difference / unlockedIndices.length;
      
      unlockedIndices.forEach(i => {
        currentAllocations[i] += adjustmentPerSlider;
        
        // Ensure no negative values
        if (currentAllocations[i] < 0) {
          const deficit = -currentAllocations[i];
          currentAllocations[i] = 0;
          
          // Redistribute the deficit to other unlocked sliders if any
          const remainingUnlocked = unlockedIndices.filter(j => j !== i && currentAllocations[j] > 0);
          if (remainingUnlocked.length > 0) {
            const redistributeAmount = deficit / remainingUnlocked.length;
            remainingUnlocked.forEach(j => {
              currentAllocations[j] -= redistributeAmount;
              if (currentAllocations[j] < 0) currentAllocations[j] = 0;
            });
          }
        }
        
        // Ensure no values over 100
        if (currentAllocations[i] > 100) {
          currentAllocations[i] = 100;
        }
      });
    }
    
    // Round to integers
    const roundedAllocations = currentAllocations.map(v => Math.round(v));
    
    // Adjust for rounding errors to ensure sum is 100
    const roundedSum = roundedAllocations.reduce((sum, val) => sum + val, 0);
    
    if (roundedSum !== 100 && unlockedIndices.length > 0) {
      // Find the first unlocked slider to adjust
      const adjustIndex = unlockedIndices[0];
      roundedAllocations[adjustIndex] += (100 - roundedSum);
      
      // Edge case: if adjustment makes a value negative, redistribute
      if (roundedAllocations[adjustIndex] < 0) {
        roundedAllocations[adjustIndex] = 0;
        // Try to find another unlocked slider to adjust
        const nextUnlocked = unlockedIndices.slice(1);
        if (nextUnlocked.length > 0) {
          roundedAllocations[nextUnlocked[0]] += (100 - roundedAllocations.reduce((sum, val) => sum + val, 0));
        }
      }
    }
    
    handleChange('allocationPreference', roundedAllocations);
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Risk Profile Assessment</h2>
      
      <div className="space-y-8">
        {/* Market Drop Scenario */}
        <div>
          <Label className="mb-2 block">
            If your investment dropped 20% in a month, what would you do?
          </Label>
          
          <RadioGroup 
            value={formData.marketDropResponse} 
            onValueChange={(value) => handleChange('marketDropResponse', value)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="sell-all" id="sell-all" />
              <label htmlFor="sell-all" className="text-sm">Sell everything to prevent further losses</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="sell-portion" id="sell-portion" />
              <label htmlFor="sell-portion" className="text-sm">Sell a portion to reduce risk</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="do-nothing" id="do-nothing" />
              <label htmlFor="do-nothing" className="text-sm">Do nothing and wait for recovery</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="buy-more" id="buy-more" />
              <label htmlFor="buy-more" className="text-sm">Buy more at the lower price</label>
            </div>
          </RadioGroup>
        </div>
        
        {/* Investment Allocation */}
        <div>
          <Label className="mb-2 block">
            How would you allocate ₹10 lakhs across different investment options?
          </Label>
          
          <div className="mb-2 text-xs text-white/60">
            Click the lock icon to fix a slider's value. Locked sliders won't auto-adjust when you move other sliders.
          </div>
          
          <Card className="bg-[#9E7C47]/5 border-[#9E7C47]/20">
            <CardContent className="p-5">
              {/* Safe Options */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm">Safe but low return options</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-6 w-6 rounded-full ${lockedSliders[0] ? 'bg-[#9E7C47]/20' : 'bg-transparent'}`}
                      onClick={() => toggleLock(0)}
                    >
                      {lockedSliders[0] ? <Check className="h-3 w-3" /> : <X className="h-3 w-3 opacity-60" />}
                    </Button>
                    <span className="text-sm font-medium text-[#9E7C47]">{formData.allocationPreference[0]}%</span>
                  </div>
                </div>
                <Slider
                  value={[formData.allocationPreference[0]]}
                  onValueChange={(value) => handleAllocationChange(0, value[0])}
                  max={100}
                  step={1}
                  className="py-2"
                  disabled={lockedSliders[0]}
                />
              </div>
              
              {/* Moderate Risk Options */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm">Moderate risk options</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-6 w-6 rounded-full ${lockedSliders[1] ? 'bg-[#9E7C47]/20' : 'bg-transparent'}`}
                      onClick={() => toggleLock(1)}
                    >
                      {lockedSliders[1] ? <Check className="h-3 w-3" /> : <X className="h-3 w-3 opacity-60" />}
                    </Button>
                    <span className="text-sm font-medium text-[#9E7C47]">{formData.allocationPreference[1]}%</span>
                  </div>
                </div>
                <Slider
                  value={[formData.allocationPreference[1]]}
                  onValueChange={(value) => handleAllocationChange(1, value[0])}
                  max={100}
                  step={1}
                  className="py-2"
                  disabled={lockedSliders[1]}
                />
              </div>
              
              {/* High Risk Options */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm">High risk, high return options</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-6 w-6 rounded-full ${lockedSliders[2] ? 'bg-[#9E7C47]/20' : 'bg-transparent'}`}
                      onClick={() => toggleLock(2)}
                    >
                      {lockedSliders[2] ? <Check className="h-3 w-3" /> : <X className="h-3 w-3 opacity-60" />}
                    </Button>
                    <span className="text-sm font-medium text-[#9E7C47]">{formData.allocationPreference[2]}%</span>
                  </div>
                </div>
                <Slider
                  value={[formData.allocationPreference[2]]}
                  onValueChange={(value) => handleAllocationChange(2, value[0])}
                  max={100}
                  step={1}
                  className="py-2"
                  disabled={lockedSliders[2]}
                />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Investment Priority */}
        <div>
          <Label className="mb-2 block">
            What's more important to you?
          </Label>
          
          <RadioGroup 
            value={formData.investmentPriority} 
            onValueChange={(value) => handleChange('investmentPriority', value)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="preserve-capital" id="preserve-capital" />
              <label htmlFor="preserve-capital" className="text-sm">Preserving capital</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="generate-income" id="generate-income" />
              <label htmlFor="generate-income" className="text-sm">Generating income</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="grow-longterm" id="grow-longterm" />
              <label htmlFor="grow-longterm" className="text-sm">Growing assets long-term</label>
            </div>
          </RadioGroup>
        </div>
        
        {/* Investment Familiarity */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>How familiar are you with different investment options?</Label>
            <span className="text-[#9E7C47] text-sm">
              {formData.investmentFamiliarity}/5
            </span>
          </div>
          
          <Slider
            id="investmentFamiliarity"
            min={1}
            max={5}
            step={1}
            value={[formData.investmentFamiliarity]}
            onValueChange={(value) => handleChange('investmentFamiliarity', value[0])}
            className="py-4"
          />
          
          <div className="flex justify-between text-xs text-white/50 mt-1">
            <span>Not familiar</span>
            <span>Somewhat familiar</span>
            <span>Very familiar</span>
          </div>
        </div>
        
        {/* Time Horizon */}
        <div>
          <Label className="mb-2 block">
            How soon might you need to access your invested money?
          </Label>
          
          <RadioGroup 
            value={formData.timeHorizon} 
            onValueChange={(value) => handleChange('timeHorizon', value)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="less-than-1-year" id="less-than-1-year" />
              <label htmlFor="less-than-1-year" className="text-sm">Less than 1 year</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="1-3-years" id="1-3-years" />
              <label htmlFor="1-3-years" className="text-sm">1-3 years</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="3-7-years" id="3-7-years" />
              <label htmlFor="3-7-years" className="text-sm">3-7 years</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="7-plus-years" id="7-plus-years" />
              <label htmlFor="7-plus-years" className="text-sm">7+ years</label>
            </div>
          </RadioGroup>
        </div>
      </div>
    </div>
  );
};

export default ProfileRiskAssessment;
